import React, { useState, useEffect } from 'react';
import walletService from '../../services/walletService';
import './RewardsModal.css';

const RewardsModal = ({ show, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [error, setError] = useState(null);
  const [claimingGameId, setClaimingGameId] = useState(null);

  useEffect(() => {
    if (show && walletService.isConnected) {
      loadRewards();
    }
  }, [show]);

  const loadRewards = async () => {
    try {
      setLoading(true);
      setError(null);
      const userRewards = await walletService.fetchRewards();
      setRewards(userRewards);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (gameId) => {
    try {
      setClaimingGameId(gameId);
      await walletService.claimReward(gameId);
      // Reload rewards after successful claim
      await loadRewards();
    } catch (err) {
      console.error('Error claiming reward:', err);
      // You could show a toast notification here
    } finally {
      setClaimingGameId(null);
    }
  };

  if (!show) return null;

  if (!walletService.isConnected) {
    return (
      <div className="modal-overlay">
        <div className="rewards-modal">
          <div className="rewards-modal-header">
            <h2>🏆 Rewards</h2>
            <button className="modal-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="rewards-modal-content">
            <div className="rewards-empty">
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ff6b6b', marginBottom: '20px' }}></i>
              <h3>Wallet Not Connected</h3>
              <p>Please connect your wallet to view your rewards</p>
              <div className="rewards-empty-actions">
                <button className="close-modal-btn" onClick={onClose}>Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const wonGames = rewards.filter(reward => reward.isWinner).length;
  const totalGames = rewards.length;
  const gamesWithWinners = rewards.filter(reward => reward.hasWinner).length;

  return (
    <div className="modal-overlay">
      <div className="rewards-modal">
        <div className="rewards-modal-header">
          <h2>🏆 Rewards</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="rewards-modal-content">
          {loading ? (
            <div className="rewards-loading">
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
              <h3>Loading Rewards...</h3>
              <p>Please wait while we fetch your game history</p>
            </div>
          ) : error ? (
            <div className="rewards-loading">
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ff6b6b', marginBottom: '20px' }}></i>
              <h3>Error Loading Rewards</h3>
              <p>{error}</p>
              <div className="rewards-empty-actions">
                <button className="close-modal-btn" onClick={onClose}>Close</button>
              </div>
            </div>
          ) : rewards.length === 0 ? (
            <div className="rewards-empty">
              <i className="fas fa-trophy" style={{ fontSize: '48px', color: '#ffd700', marginBottom: '20px' }}></i>
              <h3>No Games Played Yet</h3>
              <p>Start playing games to earn rewards!</p>
              <p>Win games to collect your prize money.</p>
              <div className="rewards-empty-actions">
                <button className="close-modal-btn" onClick={onClose}>Close</button>
              </div>
            </div>
          ) : (
            <div className="rewards-content">
              <div className="rewards-summary">
                <div className="summary-card">
                  <h3>Games Won</h3>
                  <div className="summary-value">{wonGames} / {totalGames}</div>
                </div>
                <p>View your game history ({gamesWithWinners} games with winners, {totalGames - gamesWithWinners} draws)</p>
              </div>
              
              <div className="rewards-table-container">
                <table className="rewards-table">
                  <thead>
                    <tr>
                      <th>Room ID</th>
                      <th>Date</th>
                      <th>Player</th>
                      <th>Status</th>
                      <th>Action</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rewards.map((reward, index) => (
                      <tr key={index}>
                        <td>{reward.roomId || 'N/A'}</td>
                        <td>{new Date(reward.player.joinedAt).toLocaleString()}</td>
                        <td>
                          {reward.player.address 
                            ? `${reward.player.address.slice(0, 6)}...${reward.player.address.slice(-4)}` 
                            : 'N/A'
                          }
                        </td>
                        <td>
                          {reward.isWinner ? (
                            reward.claimed && reward.claimTransaction?.transactionHash ? (
                              <span className="claimed-badge claimed">
                                Claimed {reward.claimedAt ? new Date(reward.claimedAt).toLocaleDateString() : ''}
                              </span>
                            ) : reward.claimed ? (
                              <span className="claimed-badge pending">Processing...</span>
                            ) : (
                              <span className="claimed-badge unclaimed">Unclaimed</span>
                            )
                          ) : (
                            <div>
                              <span className="game-result-badge lost">You Lost</span>
                              <br />
                              <small style={{ color: '#888', fontSize: '10px' }}>
                                {reward.winners.length > 0 
                                  ? `Winner: ${reward.winners[0].address.slice(0, 6)}...${reward.winners[0].address.slice(-4)}`
                                  : 'Game Ended'
                                }
                              </small>
                            </div>
                          )}
                        </td>
                        <td>
                          {reward.isWinner ? (
                            reward.claimed && reward.claimTransaction?.transactionHash ? (
                              <button className="claim-btn claimed" disabled>Already Claimed</button>
                            ) : (
                              <button 
                                className={`claim-btn ${reward.claimed ? 'retry' : ''}`}
                                onClick={() => handleClaimReward(reward.gameId)}
                                disabled={claimingGameId === reward.gameId}
                              >
                                {claimingGameId === reward.gameId ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Claiming...
                                  </>
                                ) : reward.claimed ? (
                                  'Retry Claim'
                                ) : (
                                  'Claim'
                                )}
                              </button>
                            )
                          ) : (
                            <span className="no-action">-</span>
                          )}
                        </td>
                        <td>
                          {reward.isWinner && reward.claimTransaction?.transactionHash ? (
                            <a 
                              href={`https://testnet.bscscan.com/tx/${reward.claimTransaction.transactionHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="receipt-link"
                            >
                              <i className="fas fa-receipt"></i> View Receipt
                            </a>
                          ) : reward.isWinner && reward.claimed ? (
                            <span className="receipt-pending">Processing...</span>
                          ) : (
                            <span className="receipt-na">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsModal;
